<template>
  <b-modal
    v-if="selectedCase"
    :id="id"
    ref="validationModal"
    ok-variant="-primary btn-sm"
    cancel-variant="-primary btn__outline btn-sm"
    :title="`${$t('component.modalConfirmation.title')} - ${selectedCase.casCasenumber}`"
    centered
    :ok-title="$t('component.modalConfirmation.okTitle')"
    :cancel-title="$t('component.modalConfirmation.cancelTitle')"
    @ok="applyValidationAndSelectNextCase(true)"
  >
    <div
      class="mb-2"
      v-html="errorMessage"
    />
    <b-form-checkbox
      v-model="status"
      :value="true"
      :unchecked-value="false"
    >
      {{ $t('diagnosis.applyToAllCases') }}
    </b-form-checkbox>
  </b-modal>
</template>

<script>
import diagnosisMixin from '@/modules/diagnosisModule/mixins/diagnosisMixin'
import { CaseOperation } from '@/api/operations/models/case-operation'
import { cloneDeep } from 'lodash'
import diagnosisCaseService from '@/modules/diagnosisModule/services/DiagnosisCaseService'
import apiExceptionType from '@/utils/apiExceptionType'
import { CaseOperationGuardMixin } from '@/api/operations/mixins/guards/case-operation-guard.mixin'

export default {
    name: 'CaseBatchValidator',
    mixins: [
        diagnosisMixin,
        CaseOperationGuardMixin
    ],
    data() {
        return {
            id: 'caseValidationModal',
            status: false,
            casesData: [],
            errorMessage: '',
            caseIndex: 0,
            selectedCase: null,
            finalErrorMessages: []
        }
    },
    computed: {
        userInformation() {
            return this.$store.getters.userInformation
        }
    },
    methods: {
        show(casesData) {
            this.caseIndex = 0
            this.casesData = casesData
            this.selectedCase = casesData[this.caseIndex]
            this.errorMessage = ''
            this.finalErrorMessages = []
            this.applyValidationAndSelectNextCase()
        },
        async applyValidationAndSelectNextCase(ignoreWarnings = false) {
            const correctPathologist = await this.checkCorrectPathologist(this.selectedCase, CaseOperation.GLOBAL_VALIDATE)
            if (correctPathologist) {
                const caseValidatedOrNextStep = await this.validateCase(ignoreWarnings)
                if (caseValidatedOrNextStep) {
                    if (this.caseIndex !== this.casesData.length - 1) {
                        this.caseIndex++
                        this.selectedCase = this.casesData[this.caseIndex]
                        await this.applyValidationAndSelectNextCase()
                    } else {
                        if (this.finalErrorMessages.length) {
                            this.showErrorModalGlobal(this.finalErrorMessages.join('<br/>'))
                        }
                        this.$emit('reloadGrid')
                        this.$_cleanComponentData()
                    }
                }
            }
        },
        async checkCorrectPathologist(caseData, operationCode = null) {
            if (this.isCorrectPathologistAssigned(operationCode,
                caseData.pathologist ? caseData.pathologist.id : null,
                this.userInformation.personnel.id,
                caseData.pathologist ? caseData.pathologist.allowsCaseValidationByOtherUsers : null,
                caseData.laboratory.labId)) {
                return true
            } else {
                const changePathologistAnswer = await this.showConfirmationModalGlobal(this.$t('diagnosis.wrongPathologist'))
                if (changePathologistAnswer) {
                    return this.updateCase({ pathologistPerId: this.userInformation?.personnel?.id })
                } else {
                    return false
                }
            }
        },
        async updateCase(data) {
            this.updateLoading(true)
            const params = cloneDeep(data)
            params.originalConcurrencyUpdatedAt = this.selectedCase.originalConcurrencyUpdatedAt
            const [err] = await this.toCall(diagnosisCaseService.updateCase(this.selectedCase.laboratory.labId, this.selectedCase.casId, params))
            this.updateLoading(false)
            if (err) {
                if (err.response && err.response.data && err.response.data.code === this.$apiExceptions.CONCURRENCY_ERROR) {
                    return this.processBasicConcurrencyConflict(this.$tc('global.case', 1), err, () => {
                        this.caseData.originalConcurrencyUpdatedAt = err.response.data.currentConcurrencyUpdatedAt
                        return this.updateCase(data)
                    })
                } else {
                    this.showErrorModalGlobal()
                    return false
                }
            }
            this.showToastSuccessGlobal(this.$t('diagnosis.caseUpdated'))
            this.$emit('updateCase')
            return true
        },
        async validateCase(ignoreWarnings = false) {
            this.$refs.validationModal.hide()
            this.updateLoading(true)
            let err
            if (ignoreWarnings) {
                [err] = await this.toCall(diagnosisCaseService.validateCaseIgnoringWarnings(this.selectedCase.laboratory.labId, this.selectedCase.casId))
            } else {
                [err] = await this.toCall(diagnosisCaseService.validateCase(this.selectedCase.laboratory.labId, this.selectedCase.casId))
            }
            this.updateLoading(false)
            if (err) {
                if (err.response && err.response.data) {
                    if (err.response.data.code === apiExceptionType.CASE_VALIDATION_ABORT) {
                        const situations = this.formatSituations(err.response.data.situations)
                        this.finalErrorMessages.push(`${this.selectedCase.casCasenumber} - ${this.$t('diagnosis.errors.caseValidationError', { situations })}`)
                        return true
                    } else if (err.response.data.code === apiExceptionType.CASE_VALIDATION_WARNING) {
                        if (this.status) {
                            await this.validateCase(true)
                            return true
                        } else {
                            const situations = this.formatSituations(err.response.data.situations)
                            this.errorMessage = `${this.$t('diagnosis.errors.caseValidationWarning', { situations })}`
                            this.$refs.validationModal.show()
                            return false
                        }
                    }
                }
                if (err?.response?.data?.messageTranslated) {
                    this.finalErrorMessages.push(err.response.data.message)
                } else {
                    this.finalErrorMessages.push(`${this.selectedCase.casCasenumber} - ${this.$t('component.modalError.title')}`)
                }
                return true
            }
            this.showToastSuccessGlobal(this.$t('diagnosis.validateSuccess'))
            return true
        }
    }
}
</script>

<style scoped>

</style>
